<template>
  <div class="appointment-summary">
    <div>
      <h4 class="mb-2">Customer Details</h4>
      <div class="d-flex">
        <div class="mr-4">
          <p>{{ current.imbpAccount.data.formattedName }}</p>
          <p v-if="email" class="mb-2">{{ email }}</p>
          <SoTextField
            ref="phone-number-input"
            v-model="phoneNumber"
            :rules="[rules.required, rules.phone]"
            label="Phone"
            type="number"
            autofocus
            @update:model-value="$emit('update:model-value', $event)"
          />
        </div>
        <div>
          <p v-if="address" data-testid="customer-address">
            {{ address }}
          </p>
        </div>
      </div>
    </div>

    <div data-testid="appointment-date-time">
      <h4 class="mb-2">Date & Time</h4>
      <p class="mb-1">{{ appointmentDate }} {{ appointmentTime }}</p>
      <SoLink @click="flowManager?.goToStep('chooseAppointment')">
        Change
      </SoLink>
    </div>

    <div data-testid="job-type">
      <h4 class="mb-2">Job Type</h4>
      <div class="mb-1">
        <p>{{ meterType }}</p>
        <p>{{ installationType }}</p>
        <p>{{ appointmentType }}</p>
      </div>
      <SoLink @click="flowManager?.goToStep('jobType')"> Change </SoLink>
    </div>

    <div>
      <h4 class="mb-2">Medical Equipment</h4>
      <div class="mb-1">
        <p data-testid="medical-equipment-answer">
          {{ extraInformation.hasMedicalEquipment ? "Yes" : "No" }}
        </p>
      </div>
      <SoLink @click="flowManager?.goToStep('extraInformation')">
        Change
      </SoLink>
    </div>

    <div>
      <h4 class="mb-2">Extra Info</h4>
      <div class="mb-1" data-testid="extra-info">
        <template
          v-if="
            extraInformation.defaultComments ||
            extraInformation.additionalComments
          "
        >
          <p v-if="extraInformation.defaultComments">
            {{ extraInformation.defaultComments }}
          </p>
          <p v-if="extraInformation.additionalComments">
            {{ extraInformation.additionalComments }}
          </p>
        </template>
        <p v-else>No</p>
      </div>
      <SoLink @click="flowManager?.goToStep('extraInformation')">
        Change
      </SoLink>
    </div>
  </div>

  <SoAlert
    title="Make sure the customer is aware of the following"
    class="mt-8"
  >
    <ul>
      <li>
        If an engineer attends the property between the times specified and they
        are unable to access the property, meters or main fuse, then the job
        won't be able to be completed and will need to be rescheduled.
      </li>
      <li>
        Please ensure that there is someone over the age of 18 in the property
        throughout the installation/appointment, even if the meters are outside.
      </li>
      <li>
        Ensure that there is a clear working area around the meters and the main
        fuse.
      </li>
    </ul>
  </SoAlert>
</template>

<script lang="ts">
  import { defineComponent, inject } from "vue"
  import dayjs from "dayjs"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoLink from "@/components/SoLink.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatDate } from "@/helpers/dateHelper"
  import rules from "@/validators/rules"
  import {
    meterTypesAES,
    installationTypesAES,
    appointmentTypesAES,
  } from "@/constants/imbp"

  export default defineComponent({
    components: {
      SoTextField,
      SoAlert,
      SoLink,
    },
    props: {
      modelValue: {
        type: String,
        default: "",
      },
      accountPhoneNumber: {
        type: String,
        default: "",
        required: true,
      },
      getFlowValues: {
        type: Object,
        required: true,
      },
    },
    emits: ["update:model-value"],
    // eslint-disable-next-line vue/component-api-style
    setup() {
      const flowManager = inject<{
        goToStep: (step: string) => void | undefined
      }>("flowManager")

      return { flowManager }
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        rules,
        formatDate,
        meterTypesAES,
        installationTypesAES,
        appointmentTypesAES,
        phoneNumber: this.accountPhoneNumber,
      }
    },
    computed: {
      meterType() {
        return meterTypesAES.find(
          (meter) =>
            meter.value === this.getFlowValues.jobType.selectedMeterTypeAes,
        )?.text
      },
      installationType() {
        return installationTypesAES.find(
          (installation) =>
            installation.value ===
            this.getFlowValues.jobType.selectedInstallationType,
        )?.title
      },
      appointmentType() {
        return appointmentTypesAES.find(
          (appointment) =>
            appointment.value ===
            this.getFlowValues.jobType.selectedAppointmentType,
        )?.title
      },
      appointmentDate() {
        return formatDate(dayjs(this.getFlowValues.appointmentSlot.date))
      },
      appointmentTime() {
        const startTime = this.getFlowValues.appointmentSlot.startTime.slice(
          0,
          5,
        )
        const endTime = this.getFlowValues.appointmentSlot.endTime.slice(0, 5)
        return startTime + " - " + endTime
      },
      extraInformation() {
        return this.getFlowValues.extraInformation
      },
      email() {
        return this.current.imbpAccount.data.contactInfo?.email
      },
      address() {
        return this.current.imbpAccount.data.address?.formattedAddress
      },
    },
  })
</script>

<style lang="css" scoped>
  .appointment-summary {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr 1fr;
  }
</style>
